import React, { useMemo, useState } from "react";
import CurrencyInput from "@/components/CurrencyInput";
import { calculateSAI, estimatePell } from "@/lib/sai";
import { amortization } from "@/lib/loans";

/**
 * CalcPanel, a compact, multi-tab calculator for use alongside an uploaded
 * document. Three tabs:
 *   - SAI Estimator
 *   - Loan Reality Check
 *   - Aid-letter quick decoder (sums grants/loans into a net-cost line)
 *
 * All math is client-side. Nothing posted to a server.
 */
export default function CalcPanel({ initialTab = "sai" }) {
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="flex flex-col h-full" data-testid="sbs-calc-panel">
      <div className="flex border-b" style={{ borderColor: "#e5e7eb", background: "#f3f4f6" }}>
        {[
          ["sai",     "SAI"],
          ["loans",   "Loans"],
          ["decoder", "Decoder"],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className="px-4 py-2.5 text-sm font-medium transition-colors"
            style={{
              color: tab === id ? "#1d4ed8" : "#6b7280",
              borderBottom: `2px solid ${tab === id ? "#1d4ed8" : "transparent"}`,
            }}
            data-testid={`sbs-calc-tab-${id}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {tab === "sai"     && <SaiTab />}
        {tab === "loans"   && <LoansTab />}
        {tab === "decoder" && <DecoderTab />}
      </div>
    </div>
  );
}

function SaiTab() {
  const [parentAGI, setParentAGI] = useState("");
  const [studentAGI, setStudentAGI] = useState("");
  const [parentAssets, setParentAssets] = useState("");
  const [n, setN] = useState(1);

  const sai = useMemo(() => calculateSAI({
    parentAGI: Number(parentAGI) || 0,
    studentAGI: Number(studentAGI) || 0,
    parentAssets: Number(parentAssets) || 0,
    numberInCollege: n,
  }), [parentAGI, studentAGI, parentAssets, n]);
  const pell = useMemo(() => estimatePell(sai), [sai]);

  return (
    <div className="space-y-4" data-testid="sbs-sai">
      <CurrencyInput id="sbs-parent-agi"  label="Parent AGI"   value={parentAGI}  onChange={setParentAGI}  placeholder="85,000" testid="sbs-sai-parent" />
      <CurrencyInput id="sbs-student-agi" label="Student AGI"  value={studentAGI} onChange={setStudentAGI} placeholder="0"     testid="sbs-sai-student" />
      <CurrencyInput id="sbs-assets"      label="Parent assets" value={parentAssets} onChange={setParentAssets} placeholder="45,000" testid="sbs-sai-assets" />
      <div>
        <label className="block text-sm mb-2" style={{ color: "#6b7280" }}>Family in college</label>
        <input type="number" min="1" value={n}
          onChange={(e) => setN(Number(e.target.value) || 1)}
          className="w-full h-12 px-3 rounded-md text-sm outline-none"
          style={{ border: "1px solid #e5e7eb", background: "#ffffff", color: "#111928" }}
          data-testid="sbs-sai-n" />
      </div>
      <div className="rounded-lg p-4 mt-3" style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}>
        <div className="text-[11px] uppercase tracking-[0.16em]" style={{ color: "#1e40af" }}>Estimated SAI</div>
        <div className="font-heading text-3xl mt-1" style={{ color: "#111928" }} data-testid="sbs-sai-value">
          ${sai.toLocaleString()}
        </div>
        <div className="text-xs mt-2" style={{ color: "#1e40af" }}>
          Pell estimate: <strong>${pell.toLocaleString()}/yr</strong> (max $7,395)
        </div>
      </div>
    </div>
  );
}

function LoansTab() {
  const [principal, setPrincipal] = useState("27000");
  const [rate, setRate] = useState(6.53);
  const [years, setYears] = useState(10);
  const r = useMemo(
    () => amortization({ principal: Number(principal) || 0, annualRate: rate, years }),
    [principal, rate, years]
  );
  return (
    <div className="space-y-4" data-testid="sbs-loans">
      <CurrencyInput id="sbs-principal" label="Loan amount" value={principal} onChange={setPrincipal} placeholder="0" testid="sbs-loan-principal" />
      <div>
        <label className="block text-sm mb-2" style={{ color: "#6b7280" }}>Rate (%)</label>
        <input type="number" step="0.01" value={rate}
          onChange={(e) => setRate(Number(e.target.value) || 0)}
          className="w-full h-12 px-3 rounded-md text-sm outline-none"
          style={{ border: "1px solid #e5e7eb", background: "#ffffff", color: "#111928" }} />
      </div>
      <div>
        <label className="block text-sm mb-2" style={{ color: "#6b7280" }}>Term (years)</label>
        <input type="number" value={years}
          onChange={(e) => setYears(Number(e.target.value) || 0)}
          className="w-full h-12 px-3 rounded-md text-sm outline-none"
          style={{ border: "1px solid #e5e7eb", background: "#ffffff", color: "#111928" }} />
      </div>
      <div className="rounded-lg p-4 mt-3" style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}>
        <div className="text-[11px] uppercase tracking-[0.16em]" style={{ color: "#1e40af" }}>Monthly</div>
        <div className="font-heading text-3xl mt-1" style={{ color: "#111928" }} data-testid="sbs-loan-monthly">
          ${r.monthly.toLocaleString()}
        </div>
        <dl className="grid grid-cols-2 gap-2 text-xs mt-3" style={{ color: "#1e40af" }}>
          <div><dt>Total paid</dt><dd className="font-medium">${r.totalPaid.toLocaleString()}</dd></div>
          <div><dt>Interest</dt><dd className="font-medium">${r.totalInterest.toLocaleString()}</dd></div>
        </dl>
      </div>
    </div>
  );
}

function DecoderTab() {
  const [coa, setCoa] = useState("");
  const [grants, setGrants] = useState("");
  const [scholarships, setScholarships] = useState("");
  const [loans, setLoans] = useState("");
  const [work, setWork] = useState("");

  const N = (s) => Number(s) || 0;
  const gift = N(grants) + N(scholarships);
  const net = Math.max(0, N(coa) - gift);
  const pctFree = N(coa) > 0 ? Math.round((gift / N(coa)) * 100) : 0;

  return (
    <div className="space-y-3" data-testid="sbs-decoder">
      <CurrencyInput id="sbs-coa"     label="Cost of attendance" value={coa}          onChange={setCoa}          placeholder="82,000" testid="sbs-dec-coa" />
      <CurrencyInput id="sbs-grants"  label="Grants"             value={grants}       onChange={setGrants}       placeholder="0"     testid="sbs-dec-grants" />
      <CurrencyInput id="sbs-scholar" label="Scholarships"       value={scholarships} onChange={setScholarships} placeholder="0"     testid="sbs-dec-scholar" />
      <CurrencyInput id="sbs-loans"   label="Loans"              value={loans}        onChange={setLoans}        placeholder="0"     testid="sbs-dec-loans" />
      <CurrencyInput id="sbs-work"    label="Work-study"         value={work}         onChange={setWork}         placeholder="0"     testid="sbs-dec-work" />

      <div className="rounded-lg p-4 mt-2" style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}>
        <div className="text-[11px] uppercase tracking-[0.16em]" style={{ color: "#1e40af" }}>Net cost / year</div>
        <div className="font-heading text-3xl mt-1" style={{ color: "#111928" }} data-testid="sbs-dec-net">
          ${net.toLocaleString()}
        </div>
        <div className="text-xs mt-3" style={{ color: "#1e40af" }}>
          {pctFree}% of COA is free money. {N(loans) > gift && "Loans exceed gift aid, read carefully."}
        </div>
      </div>
    </div>
  );
}
